import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useFetchData } from 'services/queries';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  CircularProgress,
  Box,
  Divider,
  Grid,
  Chip,
} from '@mui/material';
import { BsPencilSquare } from 'react-icons/bs';

const TemplateItemView = () => {
  const { id } = useParams();
  
  const resource = 'additional-cost/template-items';
  
  const { data: itemData, isLoading, error } = useFetchData(
    `${resource}/${id}`,
    true
  );
  
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  if (error || !itemData) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" color="error">
            Error loading template item
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {error?.message || 'Template item not found'}
          </Typography>
        </CardContent>
      </Card>
    );
  }
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      // Format as dd/MM/yyyy HH:mm using native JavaScript
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };
  
  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Template Item Details
          </Typography>
          <Typography variant="body2" color="textSecondary">
            View template item configuration
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Category
            </Typography>
            <Typography variant="body1" gutterBottom>
              <Chip 
                label={itemData.category_display || itemData.category} 
                color="primary" 
                variant="outlined" 
              />
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Subcategory
            </Typography>
            <Typography variant="body1" gutterBottom>
              {itemData.subcategory_detail ? (
                <Chip 
                  label={itemData.subcategory_detail.name} 
                  color="secondary" 
                  variant="outlined" 
                />
              ) : (
                '-'
              )}
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" gutterBottom>
              {itemData.description || '-'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Amount
            </Typography>
            <Typography variant="body1" gutterBottom>
              {itemData.amount ? new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(itemData.amount) : '-'}
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Divider />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Created At
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatDate(itemData.created_at)}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Created By
            </Typography>
            <Typography variant="body1" gutterBottom>
              {itemData.created_by_display || '-'}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Updated At
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatDate(itemData.updated_at)}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Updated By
            </Typography>
            <Typography variant="body1" gutterBottom>
              {itemData.updated_by_display || '-'}
            </Typography>
          </Grid>
        </Grid>
        
        {itemData.subcategory_detail && itemData.subcategory_detail.description && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Subcategory Description
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {itemData.subcategory_detail.description}
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
      
      <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
        <Button
          component={Link}
          to={`/admin/settings/additional-costs/template/edit/${id}`}
          variant="contained"
          color="primary"
          startIcon={<BsPencilSquare />}
        >
          Edit
        </Button>
      </CardActions>
    </Card>
  );
};

export default TemplateItemView;